import { AxiosResponse } from 'axios';
import Request from '../../api/Request';
import {
    AdminFunnelDashboardStats,
    EligibleSponsor,
    FunnelDashboardStats,
    FunnelStage,
    ProspectCreatePayload,
    SalesFunnelProspect,
    StageDetailsPayload,
    ActivityLogEntry,
} from './salesFunnel.types';

interface PaginatedResponse<T> {
    data: T[];
    links: any;
    meta: any;
}

// ──────────────────────────────────────────────
// Sponsor / Org Endpoints
// ──────────────────────────────────────────────

export interface GetProspectsParams {
    stage?: FunnelStage;
    search?: string;
    facility_type?: string;
    lost_only?: boolean;
    sort?: string;
    page?: number;
    per_page?: number;
}

export const getSalesFunnelProspects = (
    orgUuid: string,
    params?: GetProspectsParams,
): Promise<AxiosResponse<PaginatedResponse<SalesFunnelProspect>>> => {
    return Request.get(`/organizations/${orgUuid}/sales-funnel`, { params });
};

export const getSalesFunnelDashboard = (
    orgUuid: string,
): Promise<AxiosResponse<FunnelDashboardStats>> => {
    return Request.get(`/organizations/${orgUuid}/sales-funnel/dashboard`);
};

export const getSalesFunnelProspect = (
    orgUuid: string,
    prospectUuid: string,
): Promise<AxiosResponse<{ data: SalesFunnelProspect }>> => {
    return Request.get(`/organizations/${orgUuid}/sales-funnel/${prospectUuid}`);
};

export const createSalesFunnelProspect = (
    orgUuid: string,
    data: ProspectCreatePayload,
): Promise<AxiosResponse<{ data: SalesFunnelProspect }>> => {
    return Request.post(`/organizations/${orgUuid}/sales-funnel`, data);
};

export const updateSalesFunnelProspect = (
    orgUuid: string,
    prospectUuid: string,
    data: Partial<ProspectCreatePayload>,
): Promise<AxiosResponse<{ data: SalesFunnelProspect }>> => {
    return Request.put(`/organizations/${orgUuid}/sales-funnel/${prospectUuid}`, data);
};

export const advanceSalesFunnelProspect = (
    orgUuid: string,
    prospectUuid: string,
): Promise<AxiosResponse<{ data: SalesFunnelProspect }>> => {
    return Request.put(`/organizations/${orgUuid}/sales-funnel/${prospectUuid}/advance`);
};

export const moveSalesFunnelProspectStage = (
    orgUuid: string,
    prospectUuid: string,
    stage: FunnelStage,
): Promise<AxiosResponse<{ data: SalesFunnelProspect }>> => {
    return Request.put(`/organizations/${orgUuid}/sales-funnel/${prospectUuid}/move-stage`, {
        stage,
    });
};

export const updateSalesFunnelStageDetails = (
    orgUuid: string,
    prospectUuid: string,
    data: StageDetailsPayload,
): Promise<AxiosResponse<{ data: SalesFunnelProspect }>> => {
    return Request.put(
        `/organizations/${orgUuid}/sales-funnel/${prospectUuid}/stage-details`,
        data,
    );
};

export const markProspectLost = (
    orgUuid: string,
    prospectUuid: string,
    lostReason?: string,
): Promise<AxiosResponse<{ data: SalesFunnelProspect }>> => {
    return Request.post(`/organizations/${orgUuid}/sales-funnel/${prospectUuid}/lost`, {
        lost_reason: lostReason,
    });
};

export const reactivateProspect = (
    orgUuid: string,
    prospectUuid: string,
): Promise<AxiosResponse<{ data: SalesFunnelProspect }>> => {
    return Request.post(`/organizations/${orgUuid}/sales-funnel/${prospectUuid}/reactivate`);
};

export const linkProspectToOrganization = (
    orgUuid: string,
    prospectUuid: string,
    linkedOrgUuid: string,
): Promise<AxiosResponse<{ data: SalesFunnelProspect }>> => {
    return Request.post(
        `/organizations/${orgUuid}/sales-funnel/${prospectUuid}/link-organization`,
        {
            linked_organization_uuid: linkedOrgUuid,
        },
    );
};

export const getProspectActivity = (
    orgUuid: string,
    prospectUuid: string,
): Promise<AxiosResponse<PaginatedResponse<ActivityLogEntry>>> => {
    return Request.get(`/organizations/${orgUuid}/sales-funnel/${prospectUuid}/activity`);
};

// ──────────────────────────────────────────────
// Admin Endpoints
// ──────────────────────────────────────────────

export const getAdminSalesFunnelDashboard = (): Promise<
    AxiosResponse<AdminFunnelDashboardStats>
> => {
    return Request.get('/admin/sales-funnel/dashboard');
};

/**
 * Returns all orgs with an active Gold subscription — the complete eligible sponsor list.
 * Used to populate the admin "Assign to Sponsor" dropdown, including new sponsors with no prospects yet.
 */
export const getEligibleSalesFunnelSponsors = (): Promise<AxiosResponse<EligibleSponsor[]>> => {
    return Request.get('/admin/sales-funnel/eligible-sponsors');
};

export interface AdminGetProspectsParams {
    stage?: FunnelStage;
    organization_uuid?: string;
    search?: string;
    stale?: boolean;
    lost_only?: boolean;
    page?: number;
    per_page?: number;
}

export const getAdminSalesFunnelProspects = (
    params?: AdminGetProspectsParams,
): Promise<AxiosResponse<PaginatedResponse<SalesFunnelProspect>>> => {
    return Request.get('/admin/sales-funnel/prospects', { params });
};

export const getAdminSalesFunnelProspect = (
    prospectUuid: string,
): Promise<AxiosResponse<{ data: SalesFunnelProspect }>> => {
    return Request.get(`/admin/sales-funnel/prospects/${prospectUuid}`);
};

export interface AdminCreateProspectPayload extends ProspectCreatePayload {
    organization_uuid: string;
    notes?: string;
}

export const adminCreateSalesFunnelProspect = (
    data: AdminCreateProspectPayload,
): Promise<AxiosResponse<{ data: SalesFunnelProspect }>> => {
    return Request.post('/admin/sales-funnel/prospects', data);
};
